import AddOnService from '@spike/addon-service-model';
import { status as AppointmentStatuses } from '@spike/appointment-model';
import { NewAppointment } from '@spike/new-booking-model';
import {
    INITIAL_BOOKING_EDIT_SCOPE_REQUEST,
    NewBookingsStatus,
    saveCreateAppointmentThunk,
    updateBookingAppointmentsThunk
} from '@spike/new-bookings-action';
import { showSuccess } from '@spike/notifications-action';
import { Order, OrderItem, OrderizableTypes, SaveOrder } from '@spike/order-model';
import { searchEnrollmentsThunk } from '@spike/memberships-action';
import {
    addOrderItemsScopedThunk,
    INITIAL_ORDER_EDIT_SCOPE_REQUEST,
    OrdersStatus,
    reloadOrderThunk,
    saveOrderItemThunk,
    saveOrderThunk
} from '@spike/orders-action';
import { isOrderInRecurringSeries } from './isOrderInRecurringSeries';
import { Product } from '@spike/product-model';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper, useAuth, useMarketplace, useMasterData, useTimeZone } from 'hooks';
import {
    AddOnDate,
    Boarding,
    Daycare,
    Feeding,
    itemTypes,
    Medication,
    Discount as MultipleBookingDiscount,
    PetServiceWrapper,
    Training,
    Transportation,
    Walking,
    versionPetAndServices
} from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from 'store';
import {
    convertDiscount,
    getOrderItemDescription,
    getOrderItemDiscount,
    getOrderItemOrderizable,
    getPetBusinessAreas,
    getUsedPetIds,
    getUsedPetServices
} from './EditOrderItem';
import { getRoomUnitId } from 'components/CreateBooking/CreateBooking';
import PetAndServices from 'components/CreateBooking/PetAndServices';
import ComplementaryItem from 'components/CreateBooking/ExtraItems';
import { Task } from '@spike/task-model';
import Service, { resolveBookingType } from 'model/Service';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import Staff from 'model/Staff';
import {
    createTaskDate,
    createMedicationTaskDate,
    getRangeDateByMomentTypeIdWithTimezone,
    shouldCreateTaskForDay
} from 'utils/dateTimeUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import { DaySchedule } from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import { buildDaycareAppointments } from './buildDaycareAppointments';
import { buildWalkingAppointments } from './buildWalkingAppointments';
import { buildTrainingAppointments } from './buildTrainingAppointments';
import { buildTransportationAppointments } from './buildTransportationAppointments';
import {
    resolveDaycareSavePricing,
    resolveBoardingOrderItemPriceForSave,
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    resolveStandardServiceSavePricing
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import {
    customPriceDataFromBoarding,
    customPriceDataFromDaycare,
    customPriceDataFromPetService,
    customPriceDataFromTraining,
    customPriceDataFromTransportation,
    customPriceDataFromWalking,
    persistOrderItemCustomPrices
} from 'components/CreateBooking/utils/bookingCustomPrices';
import { hasMultipleDaycareDates } from 'utils/daycareDateSlots';
import { hasMultipleWalkingDates } from 'utils/walkingDateSlots';
import { hasMultipleTrainingDates } from 'utils/trainingDateSlots';
import { hasMultipleTransportationDates } from 'utils/transportationDateSlots';

export interface AddOrderItemProps {
    order: Order;
    bookingId: number;
    petId: number;
    recurrentInfo?: boolean;
    inRecurringSeries?: boolean;
    type: OrderizableTypes;
    className?: string;
    isEvaluation?: boolean;
    onClose: () => void;
}

const getType = (orderizableType: OrderizableTypes): itemTypes | undefined => {
    switch (orderizableType) {
        case OrderizableTypes.APPOINTMENT:
            return itemTypes.service;
            break;
        case OrderizableTypes.ADDON_SERVICE:
            return itemTypes.addOn;
            break;
        case OrderizableTypes.PRODUCT:
            return itemTypes.product;
            break;
        case OrderizableTypes.CUSTOM:
            return itemTypes.custom;
            break;
        default:
            return undefined;
    }
};

const DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY = 'default_tax_for_custom_items_id';
const NO_TAX_SETTING_VALUE = '0';

const getDefaultCustomItemsTaxId = (marketplace: ReturnType<typeof useMarketplace>): number | undefined => {
    const value = marketplace.settings?.find(
        setting => setting.key === DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY
    )?.value;

    if (!value || value === NO_TAX_SETTING_VALUE) {
        return undefined;
    }

    const taxId = Number(value);
    const taxExists = marketplace.taxes.some(tax => Number(tax.id) === taxId);

    return taxExists ? taxId : undefined;
};

const getUsedIds = (orderizableType: OrderizableTypes, order: Order, petId: number): Array<number> | undefined => {
    if (orderizableType === OrderizableTypes.ADDON_SERVICE) {
        return order.items.addOnServices
            .filter(item => item.orderItem.petId === petId)
            .map(item => item.addOnService.id!);
    } else if (orderizableType === OrderizableTypes.PRODUCT) {
        return order.items.products.filter(item => item.orderItem.petId === petId).map(item => item.product.id!);
    } else if (orderizableType === OrderizableTypes.CUSTOM) {
        return undefined;
    } else {
        return [];
    }
};

const getComplementaryItemProps = (
    order: Order,
    petId: number,
    orderizableType: OrderizableTypes
): {
    type?: itemTypes;
    parentId: number;
    petId?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    itemsUsed?: Array<number>;
    dates: Array<AddOnDate>;
    primaryServiceStaffId?: number;
    petBusinessAreas?: Array<string>;
} => {
    const itemType = getType(orderizableType);
    const usedIds = getUsedIds(orderizableType, order, petId);
    const primaryServiceStaffId = order.items.appointments.find(item => item.appointment.pet.id === petId)?.orderItem
        ?.staffId;

    return {
        parentId: order.client.id!,
        petId: petId,
        type: itemType,
        date: getNewServiceBookDate(order, petId),
        itemsUsed: usedIds,
        dates: order.items.addOnServices
            .filter(item => item.orderItem.petId === petId)
            .map(item => ({
                id: item.orderItem.orderizable.id!,
                date: item.orderItem.startedAt?.format('L') ?? ''
            })),
        primaryServiceStaffId,
        petBusinessAreas: getPetBusinessAreas(order, petId)
    };
};

const getNewServiceBookDate = (order: Order, petId: number) => {
    const bookDate = order.items.appointments
        .filter(item => item.appointment.pet.id === petId)
        .map(item => item.appointment.duration.to)
        .reduce<Moment | null>((lastTo, to) => (lastTo === null || lastTo?.isBefore(to) ? to.clone() : lastTo), null);

    return bookDate === null ? order.issuedAt : bookDate;
};

export const getPetParentId = (order: Order, petId: number) => {
    const pet = order.items.appointments.find(item => item.pet.id === petId)?.pet;

    return pet ? pet.clientId : order.client.id!;
};

const getSpecificFeedingMomentIds = (f: Feeding): number[] =>
    f.momentTypes && f.momentTypes.length > 0 ? f.momentTypes : Object.keys(f.specificMomentTimes || {}).map(Number);

const feedingIsSpecificTimeOnSave = (f: Feeding): boolean =>
    f.feedingScheduleType === 'specificTime' &&
    getSpecificFeedingMomentIds(f).some(m => !!String(f.specificMomentTimes?.[m] ?? '').trim());

export const AddOrderItem: FunctionComponent<AddOrderItemProps> = props => {
    const dispatch = useDispatch();
    // useStore ya no es necesario (no armamos pending redeem post-save).
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timezone = useTimeZone();
    const masterData = useMasterData();
    const auth = useAuth();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );
    const [saving, setSaving] = useState(false);
    const [service, setService] = useState<PetServiceWrapper | undefined>();

    const { morning, afternoon, evening } = getFeedingSlotTimes(marketplace, masterData);

    useEffect(() => {
        if (!props.petId) return;
        dispatch(searchEnrollmentsThunk(apiClientWrapper, '', undefined, 1, 100, undefined, props.petId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderItemSuccess:
            case OrdersStatus.SaveOrderSuccess:
                setSaving(false);
                props.onClose();
                break;
            case OrdersStatus.Error:
            case OrdersStatus.SaveOrderScopeRequired:
            case OrdersStatus.SaveOrderScopeCancelled:
                setSaving(false);
                break;
        }
    }, [ordersStatus]);

    useEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingCreateAppointmentSuccess: {
                setSaving(false);
                props.onClose();
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            }
            case NewBookingsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- cerrar y encolar redeem solo al cambiar status del create
    }, [newBookingsStatus]);

    useEffect(() => {
        if (props.isEvaluation && !evaluationService && serviceStatus !== ServicesStatus.Fetching) {
            dispatch(fetchEvaluationServiceThunk());
        }
    }, [props.isEvaluation, evaluationService, serviceStatus]);

    useEffect(() => {
        if (staffs.length === 0) {
            dispatch(fetchStaffThunk());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Convert evaluation service to PetServiceWrapper and pre-select it
    useEffect(() => {
        if (props.isEvaluation && evaluationService && evaluationService.id) {
            const duration = evaluationService.duration;
            const minutesFromSimple =
                'simple' in duration && duration.simple
                    ? duration.simple.hours * 60 + duration.simple.minutes
                    : undefined;
            const evaluationPetService: PetServiceWrapper = {
                service: evaluationService as any, // Type assertion to handle Service type mismatch
                durationInMinutes: minutesFromSimple ?? 60,
                price: evaluationService.pricing.fixedPrice?.[0]?.price || 0,
                businessArea: 'grooming',
                color: evaluationService.color ?? ''
            };
            setService(evaluationPetService);
        }
    }, [props.isEvaluation, evaluationService]);

    const saveAppointments = (entries: Array<{ appointment: NewAppointment; enrollmentId?: number }>) => {
        if (props.inRecurringSeries && entries.every(entry => entry.enrollmentId == null)) {
            dispatch(
                updateBookingAppointmentsThunk(
                    apiClientWrapper,
                    entries.map(entry => entry.appointment),
                    false,
                    INITIAL_BOOKING_EDIT_SCOPE_REQUEST
                )
            );
            setSaving(false);
            props.onClose();
            return;
        }

        entries.forEach(({ appointment, enrollmentId }) =>
            dispatch(
                saveCreateAppointmentThunk(
                    apiClientWrapper,
                    appointment,
                    enrollmentId != null ? 'Enrollment' : undefined,
                    enrollmentId
                )
            )
        );
    };

    const updateAppointments = (appointments: Array<NewAppointment>) => {
        dispatch(
            updateBookingAppointmentsThunk(
                apiClientWrapper,
                appointments,
                false,
                props.inRecurringSeries ? INITIAL_BOOKING_EDIT_SCOPE_REQUEST : undefined
            )
        );
        if (!props.inRecurringSeries) {
            dispatch(showSuccess('Appointments are being created'));
        }
    };

    const saveAppointmentHandler = (
        date: Moment | undefined,
        petId: number | undefined,
        service: PetServiceWrapper | undefined,
        staffId: number | undefined,
        discount: MultipleBookingDiscount | undefined
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromPetService(service));

        if (date && petId && service && auth.user) {
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = service.enrollment?.id;
            const membershipDiscount = resolveDiscountPayloadForSave(discount, service);
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: date.clone(),
                booking_end_at: date.clone().add(service?.durationInMinutes, 'minutes'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: service?.service.id,
                staff_id: staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: membershipDiscount?.amount,
                discount_percentage: membershipDiscount?.percentage,
                order_item_price: resolveOrderItemPriceForSave(service)
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveDaycareHandler = (
        daycare: Daycare,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        if (!auth.user) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromDaycare(daycare));

        const fallbackDate = daycare.from || getNewServiceBookDate(props.order, petId);

        if (hasMultipleDaycareDates(daycare, fallbackDate, timezone)) {
            const appointments = buildDaycareAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                daycare,
                feedings,
                medications,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                feedingSlots: { morning, afternoon, evening },
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        const items: Array<Task> = [];

        const createFeedingTasksForDay = (dayLocal: Moment) => {
            feedings?.forEach((feeding: Feeding) => {
                if (feedingIsSpecificTimeOnSave(feeding)) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentTypeId => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString?.trim()) return;
                        items.push({
                            taskDate: createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone),
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId,
                                specificTime: true
                            }
                        });
                    });
                } else {
                    feeding.momentTypes?.forEach((momentTypeId: number) => {
                        const taskDate = createTaskDate(
                            dayLocal,
                            momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                            timezone
                        );

                        items.push({
                            taskDate: taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                            }
                        });
                    });
                }
            });
        };

        const createMedicationTasksForDay = (dayLocal: Moment) => {
            medications?.forEach((medication: Medication) => {
                medication.schedule?.forEach((scheduleTime: string) => {
                    const taskDate = createMedicationTaskDate(dayLocal, scheduleTime, timezone);

                    items.push({
                        taskDate: taskDate,
                        medication: {
                            taskType: 2 as const,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage.quantity?.toString(),
                            dosageUnitId: medication.dosage.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId: getRoomUnitId(daycare.room) || daycare.roomUnitId
                        }
                    });
                });
            });
        };

        const dayLocal = daycare.from.clone().tz(timezone);
        createFeedingTasksForDay(dayLocal);
        createMedicationTasksForDay(dayLocal);

        if (daycare.from && petId && auth.user) {
            const appointmentUuid = apiClientWrapper.uuid();
            const { orderItemPrice, discountAmount, discountPercentage, enrollmentId } =
                resolveDaycareSavePricing(daycare);
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: daycare.from.clone(),
                booking_end_at: daycare.to?.clone() || daycare.from.clone().add(8, 'hours'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: daycare.service?.serviceId!,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: discountAmount,
                discount_percentage: discountPercentage,
                order_item_price: orderItemPrice,
                room_unit_id: getRoomUnitId(daycare.room) || daycare.roomUnitId,
                tasks: items,
                notes: ''
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveBoardingHandler = (
        boarding: Boarding,
        feedings: Array<Feeding>,
        medications: Array<Medication>,
        petId: number
    ) => {
        setSaving(true);
        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromBoarding(boarding));

        const items: Array<Task> = [];
        const isMultiDay = Boolean(boarding.from && boarding.to && boarding.to.isAfter(boarding.from));

        const createFeedingTasksForDay = (dayLocal: Moment) => {
            feedings.forEach(feeding => {
                if (feedingIsSpecificTimeOnSave(feeding)) {
                    getSpecificFeedingMomentIds(feeding).forEach(momentTypeId => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString?.trim()) return;
                        const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);
                        if (isMultiDay) {
                            if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(boarding.to!)) {
                                return;
                            }
                        }
                        items.push({
                            taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room),
                                specificTime: true
                            }
                        });
                    });
                } else {
                    feeding.momentTypes?.forEach(momentTypeId => {
                        const taskDate = createTaskDate(
                            dayLocal,
                            momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                            timezone
                        );
                        const rangeDate = getRangeDateByMomentTypeIdWithTimezone(
                            dayLocal,
                            momentTypeId,
                            morning,
                            afternoon,
                            evening,
                            timezone
                        );

                        if (isMultiDay) {
                            if (
                                !shouldCreateTaskForDay(
                                    taskDate,
                                    boarding.from,
                                    boarding.to!,
                                    rangeDate.from,
                                    rangeDate.to
                                )
                            ) {
                                return;
                            }
                        }

                        items.push({
                            taskDate: taskDate,
                            feeding: {
                                taskType: 1 as const,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                feedingBrand: feeding.brand,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity.unit,
                                feedingInstructions: feeding.instructions,
                                roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                            }
                        });
                    });
                }
            });
        };

        const createMedicationTasksForDay = (dayLocal: Moment) => {
            medications.flatMap(medication =>
                medication.schedule?.map(schedule => {
                    const taskDate = createMedicationTaskDate(dayLocal, schedule, timezone);

                    if (isMultiDay) {
                        if (taskDate.isBefore(boarding.from) || taskDate.isSameOrAfter(boarding.to)) {
                            return;
                        }
                    }

                    items.push({
                        taskDate,
                        medication: {
                            taskType: 2 as const,
                            medicationName: medication.name,
                            medicationMethodId: medication.methodType,
                            dosageAmount: medication.dosage.quantity?.toString(),
                            dosageUnitId: medication.dosage.unit,
                            medicationInstructions: medication.instructions,
                            roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                        }
                    });
                })
            );
        };

        const createSpecificMedicationTasks = () => {
            medications.flatMap((medication: Medication) => {
                medication.specificSchedule?.forEach((scheduleTime: DaySchedule) => {
                    if (scheduleTime.enabled) {
                        scheduleTime.doses?.forEach((dose: string) => {
                            const taskDate = createMedicationTaskDate(scheduleTime.date, dose, timezone);

                            items.push({
                                taskDate,
                                medication: {
                                    taskType: 2 as const,
                                    medicationName: medication.name,
                                    medicationMethodId: medication.methodType,
                                    dosageAmount: medication.dosage.quantity?.toString(),
                                    dosageUnitId: medication.dosage.unit,
                                    medicationInstructions: medication.instructions,
                                    roomUnitId: boarding.roomUnitId || getRoomUnitId(boarding.room)
                                }
                            });
                        });
                    }
                });
            });
        };

        if (isMultiDay) {
            const start = boarding.from.clone().tz(timezone).startOf('day');
            const end = boarding.to.clone().tz(timezone).startOf('day');
            const totalDays = end.diff(start, 'days');

            for (let i = 0; i <= totalDays; i++) {
                const currentDay = start.clone().add(i, 'days');
                createFeedingTasksForDay(currentDay);
                createMedicationTasksForDay(currentDay);
            }
            createSpecificMedicationTasks();
        } else {
            const dayLocal = boarding.from.clone().tz(timezone);
            createFeedingTasksForDay(dayLocal);
            createMedicationTasksForDay(dayLocal);
        }

        if (boarding.from && petId && auth.user) {
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = boarding.enrollment?.id;
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: boarding.from.clone(),
                booking_end_at: boarding.to?.clone() || boarding.from.clone().add(8, 'hours'),
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: boarding.service?.serviceId!,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                order_item_price: resolveBoardingOrderItemPriceForSave(boarding),
                room_unit_id: boarding.roomUnitId || getRoomUnitId(boarding.room),
                tasks: items,
                notes: '',
                details: {
                    boarding: {
                        room: boarding.room?.id,
                        notes: boarding.notes,
                        selectedOffer: boarding.service?.selectedOffer
                    }
                }
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveTransportationHandler = (transportation: Transportation, petId: number) => {
        if (!auth.user || !transportation.service) return;

        const fallbackDate =
            transportation.pickup?.time?.from ||
            transportation.dropOff?.time?.from ||
            getNewServiceBookDate(props.order, petId);

        persistOrderItemCustomPrices(
            dispatch,
            apiClientWrapper,
            petId,
            customPriceDataFromTransportation(transportation, fallbackDate, timezone)
        );

        if (hasMultipleTransportationDates(transportation, fallbackDate, timezone)) {
            const appointments = buildTransportationAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                transportation,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        const appointments = buildTransportationAppointments({
            apiClientWrapper,
            bookingId: props.bookingId,
            transportation,
            petId,
            customerId: getPetParentId(props.order, petId),
            createdByStaffId: auth.user.staffId,
            fallbackDate,
            timezone,
            status: AppointmentStatuses.BOOKED,
            marketplaceAddressId: marketplace.basics.address.id as number
        });

        saveAppointments(
            appointments.map(app => ({
                appointment: app,
                enrollmentId: (app as { redeemId?: number }).redeemId ?? transportation.enrollment?.id
            }))
        );
    };

    const saveTrainingHandler = (training: Training, petId: number | undefined) => {
        if (!auth.user || !petId || !training.service) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromTraining(training));

        const fallbackDate = training.from || getNewServiceBookDate(props.order, petId);

        if (hasMultipleTrainingDates(training, fallbackDate, timezone)) {
            const appointments = buildTrainingAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                training,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        if (training.from) {
            const bookedAt = training.from.clone().seconds(0).milliseconds(0);
            const bookingEndAt = training.to
                ? training.to.clone().seconds(0).milliseconds(0)
                : bookedAt.clone().add(training.service?.duration || 0, 'minutes');
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = training.enrollment?.id;
            const trainingItemDiscount: MultipleBookingDiscount = {
                type: (training.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
                value: training.discount?.value ?? 0
            };
            const trainingPricing = resolveStandardServiceSavePricing({
                price: training.price || training.service?.price || 0,
                enrollment: training.enrollment,
                membershipItem: training.membershipItem,
                discount: trainingItemDiscount,
                businessArea: 'training'
            });
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: bookedAt,
                booking_end_at: bookingEndAt,
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: training.service?.serviceId!,
                staff_id: training?.staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: trainingPricing.discountAmount,
                discount_percentage: trainingPricing.discountPercentage,
                order_item_price: trainingPricing.orderItemPrice,
                details: {
                    training: {
                        type: training.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                        address: training.address || '',
                        duration: training.service?.duration || 0,
                        instructions: training.service?.inHomeService ? training.instructions : undefined,
                        sessionType: resolveBookingType(training.service?.bookingType)
                    }
                },
                service_session_id: training.serviceSessionId ?? null
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveWalkingHandler = (walking: Walking, petId: number | undefined) => {
        if (!auth.user || !petId || !walking.service) {
            return;
        }

        persistOrderItemCustomPrices(dispatch, apiClientWrapper, petId, customPriceDataFromWalking(walking));

        const fallbackDate = walking.from || getNewServiceBookDate(props.order, petId);

        if (hasMultipleWalkingDates(walking, fallbackDate, timezone)) {
            const appointments = buildWalkingAppointments({
                apiClientWrapper,
                bookingId: props.bookingId,
                walking,
                petId,
                customerId: getPetParentId(props.order, petId),
                createdByStaffId: auth.user.staffId,
                fallbackDate,
                timezone,
                status: AppointmentStatuses.BOOKED,
                marketplaceAddressId: marketplace.basics.address.id as number
            });

            updateAppointments(appointments);
            props.onClose();
            return;
        }

        setSaving(true);

        if (walking.from) {
            const bookedAt = walking.from.clone().seconds(0).milliseconds(0);
            const bookingEndAt = walking.to
                ? walking.to.clone().seconds(0).milliseconds(0)
                : bookedAt.clone().add(walking.service?.duration || 0, 'minutes');
            const appointmentUuid = apiClientWrapper.uuid();
            const enrollmentId = walking.enrollment?.id;
            const walkingItemDiscount: MultipleBookingDiscount = {
                type: (walking.discount?.type === 'percentage' ? 'percentage' : 'amount') as 'amount' | 'percentage',
                value: walking.discount?.value ?? 0
            };
            const walkingPricing = resolveStandardServiceSavePricing({
                price: walking.price || walking.service?.price || 0,
                enrollment: walking.enrollment,
                membershipItem: walking.membershipItem,
                discount: walkingItemDiscount,
                businessArea: 'walking'
            });
            const newAppointment: NewAppointment = {
                booking_id: props.bookingId,
                address_id: marketplace.basics.address.id as number,
                booked_at: bookedAt,
                booking_end_at: bookingEndAt,
                customer_id: getPetParentId(props.order, petId),
                pet_id: petId,
                service_id: walking.service?.serviceId!,
                staff_id: walking?.staffId,
                status: AppointmentStatuses.BOOKED,
                uuid: appointmentUuid,
                created_by: auth.user.staffId,
                discount_amount: walkingPricing.discountAmount,
                discount_percentage: walkingPricing.discountPercentage,
                order_item_price: walkingPricing.orderItemPrice,
                details: {
                    walking: {
                        type: walking.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                        address: walking.address || '',
                        duration: walking.service?.duration || 0,
                        instructions: walking.service?.inHomeService ? walking.instructions : undefined
                    }
                },
                service_session_id: walking.serviceSessionId ?? null
            };

            saveAppointments([{ appointment: newAppointment, enrollmentId }]);
        }
    };

    const saveOrderItemHandler = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: MultipleBookingDiscount,
        quantity?: number,
        amount?: number,
        taxId?: number,
        dates?: Array<Moment>
    ) => {
        setSaving(true);

        const datesToSave = dates && dates.length > 0 ? dates : date ? [date] : [undefined];

        const buildOrderItem = (dateItem?: Moment): OrderItem => ({
            uuid: apiClientWrapper.uuid(),
            orderId: props.order.id,
            orderizable: getOrderItemOrderizable(selectedAddOn, selectedProduct, custom) ?? {
                type: OrderizableTypes.CUSTOM
            },
            petId: petId,
            staffId: staffId,
            description: getOrderItemDescription(selectedAddOn, selectedProduct, custom) ?? '',
            quantity: quantity ?? 1,
            amount: (amount ?? 0.0).toString(),
            taxId: taxId,
            discount: getOrderItemDiscount(discount),
            startedAt: dateItem?.clone()
        });

        if (isOrderInRecurringSeries(props.order)) {
            dispatch(
                addOrderItemsScopedThunk(
                    apiClientWrapper,
                    props.order.id,
                    datesToSave.map(dateItem => buildOrderItem(dateItem)),
                    INITIAL_ORDER_EDIT_SCOPE_REQUEST
                )
            );
            return;
        }

        if (datesToSave.length > 1) {
            const saveOrder: SaveOrder = {
                id: props.order.id,
                uuid: props.order.uuid,
                clientId: props.order.client.id!,
                shipToClientId: props.order.shipToClient.id!,
                notes: props.order.notes,
                items: datesToSave.map(dateItem => buildOrderItem(dateItem))
            };

            dispatch(saveOrderThunk(apiClientWrapper, saveOrder));
            return;
        }

        dispatch(saveOrderItemThunk(apiClientWrapper, buildOrderItem(datesToSave[0])));
    };

    const usedPetServicesIds: Array<number> = useMemo(
        () => getUsedPetServices(props.order, props.petId),
        [props.order, props.petId]
    );

    const usedPetIds: Array<number> = useMemo(() => getUsedPetIds(props.order), [props.order]);

    const complementaryItemProps = useMemo(
        () => getComplementaryItemProps(props.order, props.petId, props.type),
        [props.order, props.type, props.type]
    );

    return props.type == OrderizableTypes.APPOINTMENT ? (
        <PetAndServices
            parentId={props.order.client.id!}
            petId={props.petId}
            service={service}
            // An evaluation is booked for today, not after the services already in the order.
            date={props.isEvaluation ? moment().tz(timezone) : getNewServiceBookDate(props.order, props.petId)}
            versionPetAndServices={versionPetAndServices.add}
            petsUsed={usedPetIds}
            servicesUsed={usedPetServicesIds}
            discount={convertDiscount()}
            petWithoutSelect={true}
            flow={props.recurrentInfo || props.isEvaluation ? 'grooming' : undefined}
            recurrentInfo={props.recurrentInfo}
            loading={saving}
            isEvaluation={props.isEvaluation}
            onBack={props.onClose}
            onBook={saveAppointmentHandler}
            onSaveDaycare={saveDaycareHandler}
            onSaveBoarding={saveBoardingHandler}
            onSaveTransportation={saveTransportationHandler}
            onSaveWalking={saveWalkingHandler}
            onSaveTraining={saveTrainingHandler}
        />
    ) : (
        <ComplementaryItem
            {...complementaryItemProps}
            versionPetAndServices={versionPetAndServices.add}
            petWithoutSelect={true}
            taxId={props.type === OrderizableTypes.CUSTOM ? getDefaultCustomItemsTaxId(marketplace) : undefined}
            loading={saving}
            onBack={props.onClose}
            onBook={saveOrderItemHandler}
        />
    );
};

export default AddOrderItem;
